/**
 * Les animations d'un item : apparition, disparition, et la démonstration
 * jouée par le tutoriel.
 *
 * Portage des 10 derniers appels GSAP de brain.js — `displayTest` (742-777),
 * `hideTest` (809-855) et `playAnswer` (486-587). Les autres (drag,
 * clignotement, reset) sont déjà dans useBrainInteraction.
 *
 * CE QUI REMPLACE `width` / `height`
 *
 * brain.js animait `width` et `height` de ses sprites Pixi. Avec un
 * `anchor` à 0.5, changer ces deux valeurs revient à mettre le sprite à
 * l'échelle autour de son centre — sa position ne bouge pas. L'équivalent
 * exact en CSS est donc un `scale()` ajouté à la fin du `transform`, puisque
 * les sprites sont déjà centrés par `translate(-50%, -50%)`.
 *
 * On passe par un facteur d'échelle SÉPARÉ plutôt que par la taille des
 * sprites, et c'est délibéré : `size` est déjà animé ailleurs — grossissement
 * du token pendant le drag, clignotement des dropzones. Deux tweens sur la
 * même propriété se voleraient la valeur. Ici les deux canaux se composent
 * sans se marcher dessus.
 */
import gsap from "gsap"
import { computed, reactive, ref, type ComputedRef } from "vue"

import type { SceneLayout } from "@/features/brain/domain/geometry"

/** brain.js:742 et 809 — le canvas entier apparaît et disparaît en fondu. */
const FADE_DURATION = 0.75

/** brain.js:748-777 — apparition. */
const ENTER_DURATION = 0.75
const ENTER_EASE = "back.out(1.7)"
/** Décalage d'un élément au suivant, dans chaque famille. */
const STAGGER = 0.1
/** Les dropzones partent en premier ; tokens et figures ont ce retard. */
const ENTER_TOKENS_DELAY = 0.35

/** brain.js:814-855 — disparition. */
const LEAVE_DURATION = 0.75
const LEAVE_EASE = "expo.out"
/** Masques et dropzones sortent après tokens et figures. */
const LEAVE_MASKS_DELAY = 0.5

/** brain.js:493-494 — rythme de la démonstration du tutoriel. */
const ANSWER_STEP_DURATION = 2
const ANSWER_STEP_GAP = 0.15
/** brain.js:509 — le premier déplacement ne part qu'au bout d'une seconde. */
const ANSWER_FIRST_DELAY = 1
/**
 * Attente entre la fin de la dernière étape et la remise à zéro.
 *
 * brain.js:577-587 planifie ce `setTimeout` depuis le DÉBUT de la
 * démonstration, à `(2 + 0.15) * (n + 1) + 1` secondes. La dernière étape se
 * terminant à `(n - 1) * 2.15 + 1 + 2`, il reste `2.15 * 2 - 2 = 2.3` s, quel
 * que soit le nombre d'étapes.
 */
const ANSWER_RESET_DELAY = 2.3

/** Une étape de la réponse jouée : quel token va dans quelle dropzone. */
export type AnswerStep = { token: string; dropzone: string }

/**
 * Ce que la composable attend de l'interaction pour jouer la démonstration.
 * Volontairement réduit au strict nécessaire : `playAnswer` n'a pas besoin de
 * connaître la machine à états du drag.
 */
export type AnswerDriver = {
  /** Centre d'une dropzone, en px de scène. Null si elle n'existe pas. */
  dropzoneCenter: (_name: string) => { x: number; y: number } | null
  /**
   * L'objet réactif du token, que GSAP anime DIRECTEMENT.
   *
   * On expose l'objet plutôt qu'un `moveToken(x, y)` : brain.js interpole la
   * position sur 2 secondes en `expo.out`, il ne la fixe pas. Passer par un
   * setter donnerait une téléportation.
   */
  tokenState: (
    _name: string
  ) => { key: string; x: number; y: number; opacity: number } | null
  /** Dépose le token dans la dropzone : c'est ce qui déclenche la fusion. */
  dropToken: (_tokenName: string, _dropzoneName: string) => void
  /** Arrête le clignotement d'une dropzone. */
  stopBlink: (_dropzoneName: string) => void
  /** Remet la scène à zéro — équivalent de rawResetTest. */
  reset: () => void
}

export type BrainAnimation = ReturnType<typeof useBrainAnimation>

export function useBrainAnimation(layout: ComputedRef<SceneLayout>) {
  /** Opacité de toute la scène — brain.js animait celle du canvas. */
  const sceneOpacity = ref(1)

  /**
   * Facteur d'échelle par élément, indexé par clé de sprite (ou nom de masque).
   * Absent = 1, ce qui laisse le rendu statique strictement inchangé : c'est
   * ce qui garantit que la sonde de parité mesure toujours la même chose.
   */
  const scales = reactive<Record<string, number>>({})

  /** Le doigt du tutoriel suit le token en cours de déplacement. */
  const fingerTokenKey = ref<string | null>(null)

  const isPlayingAnswer = ref(false)

  function scaleOf(key: string): number {
    return scales[key] ?? 1
  }

  /**
   * Pose l'état de départ de l'animation d'entrée, SANS l'animer.
   *
   * À appeler dès la construction de la scène. Sans ça, la scène est rendue à
   * pleine opacité pendant la demi-seconde d'attente qui précède `enter()`
   * (brain.js:733) : elle s'affiche, disparaît, puis réapparaît en fondu.
   * brain.js n'avait pas ce défaut, son canvas étant déjà invisible à ce
   * moment-là.
   */
  function prepare() {
    sceneOpacity.value = 0
  }

  /** Tue tous les tweens en cours et remet chaque canal à sa valeur de repos. */
  function reset() {
    gsap.killTweensOf(scales)
    gsap.killTweensOf(sceneOpacity)
    for (const key of Object.keys(scales)) delete scales[key]
    sceneOpacity.value = 1
    fingerTokenKey.value = null
    isPlayingAnswer.value = false
  }

  /** Les clés animées, dans l'ordre où brain.js parcourait ses tableaux. */
  const families = computed(() => ({
    tokens: layout.value.tokens.map((t) => t.key),
    figures: layout.value.orderedFigures.map((f) => f.key),
    dropzones: layout.value.orderedDropzones.map((d) => d.key),
    masks: layout.value.masks.map((m) => m.name),
  }))

  /**
   * Anime une famille en cascade.
   *
   * `from` reproduit `gsap.from` (l'élément part de `value` et rejoint 1),
   * sinon c'est un `gsap.to` (il quitte 1 pour `value`).
   */
  function stagger(
    keys: string[],
    value: number,
    opts: { duration: number; ease: string; delay: number; from: boolean }
  ) {
    keys.forEach((key, i) => {
      const vars = {
        duration: opts.duration,
        ease: opts.ease,
        delay: i * STAGGER + opts.delay,
      }
      if (opts.from) {
        scales[key] = value
        gsap.to(scales, { ...vars, [key]: 1 })
      } else {
        if (scales[key] === undefined) scales[key] = 1
        gsap.to(scales, { ...vars, [key]: value })
      }
    })
  }

  /** Durée totale d'une cascade, pour savoir quand elle est finie. */
  function totalDuration(count: number, duration: number, delay: number) {
    return count === 0 ? 0 : (count - 1) * STAGGER + delay + duration
  }

  /**
   * brain.js:739-777 — apparition de l'item.
   *
   * Les masques ne sont PAS animés ici : seule leur opacité, portée par le
   * fondu de la scène, les fait apparaître. C'est le comportement d'origine.
   */
  function enter(): Promise<void> {
    const { tokens, figures, dropzones } = families.value

    sceneOpacity.value = 0
    gsap.to(sceneOpacity, { duration: FADE_DURATION, value: 1 })

    const shared = { duration: ENTER_DURATION, ease: ENTER_EASE, from: true }
    stagger(dropzones, 0, { ...shared, delay: 0 })
    stagger(tokens, 0, { ...shared, delay: ENTER_TOKENS_DELAY })
    stagger(figures, 0, { ...shared, delay: ENTER_TOKENS_DELAY })

    const end = Math.max(
      FADE_DURATION,
      totalDuration(dropzones.length, ENTER_DURATION, 0),
      totalDuration(tokens.length, ENTER_DURATION, ENTER_TOKENS_DELAY),
      totalDuration(figures.length, ENTER_DURATION, ENTER_TOKENS_DELAY)
    )
    return wait(end)
  }

  /**
   * brain.js:806-855 — disparition de l'item.
   *
   * Ici les masques SONT animés, et après tokens et figures. Noter que
   * brain.js met à l'échelle le conteneur du masque, dont les figures et
   * dropzones sont enfants : elles sont donc réduites deux fois. La forme
   * grise, elle, est un FRÈRE du conteneur et ne bouge pas — d'où le
   * conteneur d'enfants distinct dans BrainScene.
   *
   * `hideTest` attend ensuite 750 ms (brain.js:857), soit exactement la durée
   * d'un tween, sans tenir compte des décalages : la scène est donc masquée
   * alors que les dernières sorties ne sont pas terminées. Comportement
   * d'origine, conservé — c'est le fondu qui rend la coupure invisible.
   */
  function leave(): Promise<void> {
    const { tokens, figures, dropzones, masks } = families.value

    gsap.to(sceneOpacity, { duration: FADE_DURATION, value: 0 })

    const shared = { duration: LEAVE_DURATION, ease: LEAVE_EASE, from: false }
    stagger(tokens, 0, { ...shared, delay: 0 })
    stagger(figures, 0, { ...shared, delay: 0 })
    stagger(masks, 0, { ...shared, delay: LEAVE_MASKS_DELAY })
    stagger(dropzones, 0, { ...shared, delay: LEAVE_MASKS_DELAY })

    return wait(LEAVE_DURATION)
  }

  /**
   * brain.js:486-587 — le tutoriel joue la bonne réponse.
   *
   * Chaque étape déplace un token vers sa dropzone, le doigt accroché à lui.
   * À l'arrivée le token s'efface et la fusion s'applique : c'est la dropzone
   * qui porte désormais le résultat.
   *
   * Ce qui reste à l'appelant : neutraliser les interactions pendant la
   * démonstration et afficher le message « voici la réponse ». Ce sont des
   * responsabilités de vue, pas d'animation.
   */
  async function playAnswer(
    steps: AnswerStep[],
    driver: AnswerDriver
  ): Promise<void> {
    isPlayingAnswer.value = true

    for (const [i, step] of steps.entries()) {
      if (!isPlayingAnswer.value) return
      await wait(i === 0 ? ANSWER_FIRST_DELAY : ANSWER_STEP_GAP)
      if (!isPlayingAnswer.value) return

      const center = driver.dropzoneCenter(step.dropzone)
      const token = driver.tokenState(step.token)
      if (!center || !token) continue

      driver.stopBlink(step.dropzone)
      // La clé vient du token retrouvé, et non du nom : deux tokens peuvent
      // porter le même nom, et la clé seule les distingue.
      fingerTokenKey.value = token.key

      await new Promise<void>((resolve) => {
        gsap.to(token, {
          x: center.x,
          y: center.y,
          duration: ANSWER_STEP_DURATION,
          ease: "expo.out",
          onComplete: () => resolve(),
        })
      })
      if (!isPlayingAnswer.value) return

      // brain.js:515 — le token s'efface à l'arrivée, c'est la dropzone qui
      // porte désormais le résultat de la fusion.
      token.opacity = 0
      fingerTokenKey.value = null
      driver.dropToken(step.token, step.dropzone)
    }

    await wait(ANSWER_RESET_DELAY)
    if (!isPlayingAnswer.value) return

    driver.reset()
    isPlayingAnswer.value = false
  }

  /** Interrompt la démonstration en cours. */
  function stopAnswer() {
    isPlayingAnswer.value = false
    fingerTokenKey.value = null
  }

  return {
    sceneOpacity,
    prepare,
    scaleOf,
    fingerTokenKey,
    isPlayingAnswer,
    enter,
    leave,
    playAnswer,
    stopAnswer,
    reset,
  }
}

/**
 * Attente pilotée par GSAP plutôt que par `setTimeout`.
 *
 * Ça compte : dans un onglet masqué le navigateur bride `requestAnimationFrame`
 * et les tweens n'avancent plus, alors qu'un `setTimeout` continue. Passer par
 * la même horloge garantit que la promesse se résout quand l'animation est
 * réellement finie, pas avant.
 */
function wait(seconds: number): Promise<void> {
  if (seconds <= 0) return Promise.resolve()
  return new Promise((resolve) => {
    gsap.delayedCall(seconds, resolve)
  })
}
